import { Injectable } from '@angular/core';
import { State, Action, StateContext } from '@ngxs/store';
import { nth } from 'lodash-es';
import produce from 'immer';
import { Selector } from '@ngxs/store';
import { PaymentMethodsState, PaymentMethodsStateModel } from '../../payment-methods/payment-methods.state';
import { OrderWizardDataStateModel } from '../order-wizard-data/order-wizard-data.model';
import { OrderWizardDataState } from '../order-wizard-data/order-wizard-data.state';
import { StepConfirmations } from './order-wizard-navigation.model';

import {
  ResetOrderWizardNavigation,
  EvaluateActiveIndexFromUrl,
  SetActiveStepConfirmation,
  SetSkipBillingStep,
} from './order-wizard-navigation.actions';
import { OrderWizardNavigationStateModel, WizardStep } from './order-wizard-navigation.model';

const defaultSteps: WizardStep[] = [
  {
    key: 'insurance',
    url: 'insurance',
    title: 'Verify Insurance Information',
  },
  {
    key: 'products',
    url: 'products',
    title: 'Verify Product Information',
  },
  {
    key: 'provider',
    url: 'provider',
    title: 'Verify Provider Information',
  },
  {
    key: 'contacts',
    url: 'contacts',
    title: 'Verify Shipping Information',
  },
  {
    key: 'billing',
    url: 'billing',
    title: 'Verify Payment Information',
  },
  {
    key: 'summary',
    url: 'summary',
    title: 'Review & Authorize',
  },
];

export const navigationDefaults = {
  steps: defaultSteps,
  activeIndex: 0,
  stepConfirmations: {},
  lastConfirmedIndex: -1,
};

@State<OrderWizardNavigationStateModel>({
  name: 'orderWizardNavigation',
  defaults: navigationDefaults,
})
@Injectable({ providedIn: 'root' })
export class OrderWizardNavigationState {
  constructor() {}

  @Selector()
  static steps(state: OrderWizardNavigationStateModel): WizardStep[] {
    return state.steps;
  }

  @Selector([OrderWizardDataState, PaymentMethodsState])
  static shouldSkipForOptionalPayment(
    state: OrderWizardNavigationStateModel,
    orderWizardState: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): boolean {
    const paymentOptional = OrderWizardDataState.paymentOptional(orderWizardState, paymentMethodsState);
    const allowDeductibleOverrides = OrderWizardDataState.allowDeductibleOverrides(orderWizardState);
    return paymentOptional && !allowDeductibleOverrides;
  }

  @Selector([OrderWizardDataState, PaymentMethodsState])
  static paymentOptionalCorrectedIndex(
    state: OrderWizardNavigationStateModel,
    orderWizardState: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): number {
    const billingStepIndex = state.steps.findIndex((s) => s.key === 'billing');
    const shouldSkip = OrderWizardNavigationState.shouldSkipForOptionalPayment(
      state,
      orderWizardState,
      paymentMethodsState
    );

    return shouldSkip && billingStepIndex >= 0 && state.activeIndex > billingStepIndex
      ? state.activeIndex - 1
      : state.activeIndex;
  }

  @Selector([OrderWizardDataState, PaymentMethodsState])
  static stepCount(
    state: OrderWizardNavigationStateModel,
    orderWizardState: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): number {
    const shouldSkip = OrderWizardNavigationState.shouldSkipForOptionalPayment(
      state,
      orderWizardState,
      paymentMethodsState
    );
    return shouldSkip ? state.steps.length - 1 : state.steps.length;
  }

  @Selector()
  static stepConfirmations(state: OrderWizardNavigationStateModel): StepConfirmations {
    return state.stepConfirmations;
  }

  @Selector()
  static activeIndex(state: OrderWizardNavigationStateModel): number {
    return state.activeIndex;
  }

  @Selector()
  static lastConfirmedIndex(state: OrderWizardNavigationStateModel): number {
    return state.lastConfirmedIndex;
  }

  @Selector()
  static activeStep(state: OrderWizardNavigationStateModel): WizardStep {
    return state.steps[state.activeIndex];
  }

  @Selector()
  static previousStep(state: OrderWizardNavigationStateModel): WizardStep | null {
    return state.activeIndex > 0 ? state.steps[state.activeIndex - 1] : null;
  }

  @Selector([OrderWizardDataState, PaymentMethodsState])
  static nextStep(
    state: OrderWizardNavigationStateModel,
    orderWizardState: OrderWizardDataStateModel,
    paymentMethodsState: PaymentMethodsStateModel
  ): WizardStep | null {
    if (state.activeIndex >= state.steps.length - 1) return null;
    const shouldSkip = OrderWizardNavigationState.shouldSkipForOptionalPayment(
      state,
      orderWizardState,
      paymentMethodsState
    );
    const nextStep = state.steps[state.activeIndex + 1];
    const twoStepsFromCurrent = state.steps[state.activeIndex + 2] || null;
    return shouldSkip && nextStep.key === 'billing' ? twoStepsFromCurrent : nextStep;
  }

  setActiveIndex(ctx: StateContext<OrderWizardNavigationStateModel>, index: number) {
    ctx.setState(
      produce((draft) => {
        draft.activeIndex = index;
      })
    );

    this.updateStepperNavigation(ctx);
  }

  updateStepperNavigation(ctx: StateContext<OrderWizardNavigationStateModel>) {
    const state = ctx.getState();

    // Recompute last confirmed step
    let lastConfirmedIndex = -1;

    for (let [i, step] of state.steps.entries()) {
      if (state.stepConfirmations[step.key]) lastConfirmedIndex = i;
      else break;
    }

    ctx.setState(
      produce((draft) => {
        draft.lastConfirmedIndex = lastConfirmedIndex;
      })
    );
  }

  updateConfirmationAt(ctx: StateContext<OrderWizardNavigationStateModel>, stepKey: string, isConfirmed: boolean) {
    ctx.setState(
      produce((draft) => {
        draft.stepConfirmations[stepKey] = isConfirmed;
        // ensure that all future steps are un-checked if isConfirmed is false
        if (!isConfirmed) {
          const step = draft.steps.find((step) => step.key === stepKey);
          if (!step) return;
          draft.steps
            .slice(draft.steps.indexOf(step) + 1, draft.steps.length)
            .forEach((step) => (draft.stepConfirmations[step.key] = false));
        }
      })
    );

    this.updateStepperNavigation(ctx);
  }

  @Action(EvaluateActiveIndexFromUrl)
  evaluateActiveIndexFromUrl(ctx: StateContext<OrderWizardNavigationStateModel>, action: EvaluateActiveIndexFromUrl) {
    const foundIndex = ctx.getState().steps.findIndex((s) => s.url === nth(action.url.split('/'), -1));

    this.setActiveIndex(ctx, foundIndex !== -1 ? foundIndex : 0);
  }

  @Action(SetActiveStepConfirmation)
  setActiveStepConfirmation(ctx: StateContext<OrderWizardNavigationStateModel>, action: SetActiveStepConfirmation) {
    const state = ctx.getState();
    this.updateConfirmationAt(ctx, state.steps[state.activeIndex].key, action.isConfirmed);
  }

  @Action(SetSkipBillingStep)
  setSkipBillingStep(ctx: StateContext<OrderWizardNavigationStateModel>, action: SetSkipBillingStep) {
    ctx.setState(
      produce((draft) => {
        // think about whether to update active index or last confirmed index...
        // ... but this shouldn't be an issue because billing step will not be skipped while
        // on the billing or summary steps
        if (action.skipBilling) {
          draft.steps = defaultSteps.filter((step) => step.key !== 'billing');
        } else {
          draft.steps = defaultSteps;
        }
      })
    );
  }

  @Action(ResetOrderWizardNavigation)
  reset(ctx: StateContext<OrderWizardNavigationStateModel>) {
    ctx.setState(
      produce((draft) => {
        Object.assign(draft, navigationDefaults);
      })
    );
  }
}
